import { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { UserPlus, Loader2, Upload, FileText, CheckCircle2, Copy, Mail } from "lucide-react";
import BulkImportPanel from "@/components/BulkImportPanel";

export const OnboardClientDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form"); // form | uploads
  const [mode, setMode] = useState("single"); // single | bulk
  const [bulkDone, setBulkDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", business_name: "" });
  const [creating, setCreating] = useState(false);
  const [client, setClient] = useState(null); // {user_id, case_id, email, invite_link, invite_sent}
  const [files, setFiles] = useState([]); // uploaded refs
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStep("form");
    setMode("single");
    setBulkDone(false);
    setForm({ name: "", email: "", business_name: "" });
    setClient(null);
    setFiles([]);
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) { if (client || bulkDone) onCreated?.(); reset(); }
  };

  const createClient = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/staff/clients", {
        name: form.name.trim(), email: form.email.trim(),
        business_name: form.business_name.trim() || null,
      });
      setClient(data);
      setStep("uploads");
      toast.success(data.invite_sent ? `Invite emailed to ${data.email}` : "Client created — email not configured, copy the invite link");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create client");
    } finally { setCreating(false); }
  };

  const uploadFiles = async (fileList) => {
    if (!client || !fileList?.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", "other");
      try {
        const { data } = await api.post(`/staff/cases/${client.case_id}/client-documents`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((p) => [...p, data]);
        ok += 1;
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (ok > 0) toast.success(`${ok} document${ok === 1 ? "" : "s"} uploaded to the client's portal`);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(client.invite_link);
    toast.success("Invite link copied");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="onboard-client-btn" className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
          <UserPlus className="h-4 w-4 mr-2" /> Onboard client
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="onboard-client-dialog">
        {step === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">{mode === "bulk" ? "Bulk import clients" : "Onboard a new client"}</DialogTitle>
              <DialogDescription>
                {mode === "bulk"
                  ? "Upload a CSV of past clients. Every row gets a portal account and a set-password invite."
                  : "Create their portal and email a secure link to set their password. You can upload their documents next."}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-1 bg-[#F4F5F3] rounded-full p-1 mt-1" data-testid="onboard-mode-tabs">
              {[["single", "One client"], ["bulk", "Bulk CSV"]].map(([k, l]) => (
                <button key={k} onClick={() => setMode(k)} data-testid={`onboard-mode-${k}`}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-colors ${mode === k ? "bg-white text-[#114A31] shadow-sm" : "text-[#595D5A] hover:text-[#114A31]"}`}>
                  {l}
                </button>
              ))}
            </div>
            {mode === "bulk" ? (
              <BulkImportPanel onDone={() => setBulkDone(true)} />
            ) : (
            <div className="space-y-4 mt-2">
              <div>
                <Label htmlFor="oc-name">Client name</Label>
                <Input id="oc-name" data-testid="onboard-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="oc-email">Email</Label>
                <Input id="oc-email" data-testid="onboard-email" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@business.com" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="oc-biz">Business name <span className="text-[#9aa]">(optional)</span></Label>
                <Input id="oc-biz" data-testid="onboard-business" value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Jane Doe LLC" className="mt-1" />
              </div>
              <Button onClick={createClient} disabled={creating} data-testid="onboard-create-btn"
                className="w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Create & send invite
              </Button>
            </div>
            )}
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-[#166534]" /> {client.name} is set up
              </DialogTitle>
              <DialogDescription>
                {client.invite_sent
                  ? `A set-password invite was emailed to ${client.email}.`
                  : "Email isn't configured — copy the invite link below and send it to the client."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2 bg-[#F9F9F8] border border-[#E5E5E5] rounded-xl p-2">
                <span className="flex-1 truncate text-xs text-[#595D5A]" data-testid="onboard-invite-link">{client.invite_link}</span>
                <Button onClick={copyLink} variant="outline" size="sm" data-testid="onboard-copy-link"
                  className="rounded-full border-[#E5E5E5] shrink-0"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy</Button>
              </div>

              <div>
                <Label>Upload the client's documents</Label>
                <p className="text-xs text-[#595D5A] mt-0.5">These appear in the client's portal right away. PDF, PNG, JPG, DOC, DOCX · max 10&nbsp;MB each.</p>
                <input ref={inputRef} type="file" multiple data-testid="onboard-file-input"
                  onChange={(e) => uploadFiles(e.target.files)}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" className="hidden" />
                <Button onClick={() => inputRef.current?.click()} disabled={uploading} variant="outline"
                  data-testid="onboard-upload-btn" className="mt-2 w-full rounded-full border-dashed border-[#114A31] text-[#114A31] hover:bg-[#E6F0EB]">
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Choose files to upload
                </Button>
              </div>

              {files.length > 0 && (
                <ul className="space-y-2" data-testid="onboard-files-list">
                  {files.map((f) => (
                    <li key={f.file_id} className="flex items-center gap-2 text-sm bg-[#E6F0EB] rounded-xl px-3 py-2">
                      <FileText className="h-4 w-4 text-[#114A31]" />
                      <span className="flex-1 truncate text-[#114A31]">{f.original_filename}</span>
                      <CheckCircle2 className="h-4 w-4 text-[#166534]" />
                    </li>
                  ))}
                </ul>
              )}

              <Button onClick={() => handleOpenChange(false)} data-testid="onboard-done-btn"
                className="w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardClientDialog;
